import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import type { GalleryEventFormValues } from '@/types/gallery';

interface BasicFieldsProps {
  form: UseFormReturn<GalleryEventFormValues>;
}

export const BasicFields = ({ form }: BasicFieldsProps) => {
  const renderTooltip = (content: string) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <HelpCircle className="h-4 w-4 text-gray-400 cursor-help hover:text-gray-600 transition-colors" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p>{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="title_en"
        rules={{ required: 'English title is required' }}
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-2">
              <FormLabel>Title (English)</FormLabel>
              {renderTooltip('Use a clear, descriptive title that includes relevant keywords for better SEO')}
            </div>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="title_ko"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-2">
              <FormLabel>Title (Korean)</FormLabel>
              {renderTooltip('Provide an accurate Korean translation that maintains the meaning of the English title')}
            </div>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description_en"
        rules={{ required: 'English description is required' }}
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-2">
              <FormLabel>Description (English)</FormLabel>
              {renderTooltip('Write a detailed description including key information about the event, its purpose, and outcomes')}
            </div>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description_ko"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-2">
              <FormLabel>Description (Korean)</FormLabel>
              {renderTooltip('Ensure the Korean description captures all important details from the English version')}
            </div>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};